import clsx from '../utils/clsx';
import styles from '../styles/Battlefield.module.css';
import MaterialEditor from './MaterialEditor';
import SurfaceEditor from './SurfaceEditor';
import ShapePanel from './battlefieldBuild/ShapePanel';
import StructurePanel from './battlefieldBuild/StructurePanel';
import ViewOptions from './battlefieldBuild/ViewOptions';
import { VolumePanel, MaterialPanel, SurfacePanel, ArenaPanel, Clipboard } from './battlefieldBuild/BuildPanels';

/**
 * WHICH OPTIONS A TOOL ACTUALLY HAS.
 *
 * Contextual, mirroring the rail: pick a tool on the left, its options
 * appear along the top and nothing irrelevant is ever on screen. Erase
 * has no material, a zone has no volume, and Select has neither; Dig's
 * material is what the hole is lined with.
 *
 * `view` is on every one of them, and is the only item outside Build -
 * how the map is DRAWN is a question you have while fighting too.
 */
const TOOL_ITEMS = {
  structure: ['structure', 'material', 'arena', 'view'],
  select: ['clipboard', 'arena', 'view'],
  zone: ['shape', 'surface', 'arena', 'view'],
  fill: ['shape', 'volume', 'material', 'arena', 'view'],
  // A hole is lined with what it is dug in (plan map-display, F): its walls and floor take the material.
  dig: ['shape', 'volume', 'material', 'arena', 'view'],
};

function itemsFor(tool, mode) {
  if (mode !== 'build') return ['view'];
  return TOOL_ITEMS[tool] ?? ['shape', 'volume', 'arena', 'view'];
}

const LABELS = {
  shape: 'Shape',
  volume: 'Volume',
  material: 'Material',
  surface: 'Surface',
  arena: 'Arena',
  structure: 'Shape',
  clipboard: 'Clipboard',
  view: 'View',
};

const HINTS = {
  shape: 'what this drag lays down',
  volume: 'how tall, and where it sits',
  material: 'what it is made of',
  surface: 'ground that does something',
  arena: 'the floor everything stands on',
  structure: 'pillars, cones and domes',
  clipboard: 'copy a region and stamp it again',
  view: 'how the map is drawn',
};

/** Each item's panel under the bar. */
const PANELS = {
  shape: ShapePanel,
  volume: VolumePanel,
  material: MaterialPanel,
  surface: SurfacePanel,
  arena: ArenaPanel,
  structure: StructurePanel,
  clipboard: Clipboard,
  view: ViewOptions,
};

/**
 * THE EDITORS DROP DOWN, they do not live in the row. The sub-bar is one
 * scrolling line of controls so that its height is fixed and the combat
 * log knows where to hang from - and a material editor is far taller than
 * a line. Rendered below the row it escapes the row's clipping and behaves
 * like the flyout it always wanted to be.
 */
function Flyout({ active, build }) {
  if (active === 'material') {
    return (
      <div className={styles.optionFlyout} data-testid="option-flyout">
        <MaterialEditor
          materials={build.materials}
          selected={build.material}
          onChange={build.editMaterial}
          onAdd={build.addMaterial}
          onDelete={build.deleteMaterial}
        />
      </div>
    );
  }
  if (active !== 'surface') return null;
  return (
    <div className={styles.optionFlyout} data-testid="option-flyout">
      <SurfaceEditor
        surfaces={build.surfaces}
        selected={build.zonePreset}
        onChange={build.updateSurface}
        onAdd={build.addSurface}
        onDelete={build.deleteSurface}
      />
    </div>
  );
}

/**
 * The row of items, and the button to the other view of the same state:
 * the bar is for the change you make between one drag and the next, the
 * editor for sitting down and authoring. Neither owns anything.
 */
function OptionItems({ items, active, build, onActive, onOpenEditor }) {
  return (
    <div className={styles.optionItems} role="tablist" aria-label="Build options">
      {items.map((key) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={active === key}
          data-testid={`option-${key}`}
          className={clsx(styles.optionItem, active === key && styles.optionItemActive)}
          onClick={() => onActive?.(key)}
          title={HINTS[key]}
        >
          {key === 'volume' && build.tool === 'dig' ? 'Depth' : LABELS[key]}
        </button>
      ))}
      <span className={styles.spacer} />
      <button
        type="button"
        className={styles.optionItem}
        data-testid="open-terrain-editor"
        onClick={onOpenEditor}
        title="Materials, surfaces and the arena, with room to work in"
      >
        ✎ Editor…
      </button>
    </div>
  );
}

/**
 * BattlefieldOptionBar - the build options, as bars.
 *
 * They were a stack of collapsible sections in a drawer under the map,
 * which meant the things you change between one drag and the next were
 * two clicks and a scroll away. This is the same controls as a rail's
 * worth of items along the top with one sub-bar beneath - the shape you
 * get by turning the vertical tool rail on its side, which is exactly
 * what it is for (v8.0.0).
 *
 * It owns nothing. Every control here writes `build` or the view
 * preferences, which is what lets the terrain editor modal be a second
 * view of the same state rather than a second copy of it.
 */
export default function BattlefieldOptionBar({
  build,
  boundary,
  faceCount,
  mode,
  view,
  active: wanted,
  onActive,
  onOpenEditor,
}) {
  const items = itemsFor(build.tool, mode);
  // The tool can take the open item away - Erase has no Material - so
  // what is SHOWN is the wanted one only while it is still on offer.
  const active = items.includes(wanted) ? wanted : items[0];
  const Panel = PANELS[active];
  return (
    <div className={styles.optionBar} data-testid="battlefield-build">
      <OptionItems items={items} active={active} build={build} onActive={onActive} onOpenEditor={onOpenEditor} />
      <div className={styles.optionPanel} data-testid={`option-panel-${active}`} role="tabpanel">
        <Panel build={build} boundary={boundary} faceCount={faceCount} view={view} />
      </div>
      <Flyout active={active} build={build} />
    </div>
  );
}
